import React, { useCallback, useEffect, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import services from '../../services'
import Blocos from '../../components/SubMenuAi/Blocos'
import ChatLegado from '../../components/SubMenuAi/Legado'
import { Andamento, Conversa, Saldo, useConversaOneflow } from '../../components/SubMenuAi/Oneflow'
import '../../components/SubMenuAi/style.scss'
import './oneflow.scss'

type Estado = { chave: string } & ({ tipo: 'carregando' } | { tipo: 'erro'; mensagem: string } | { tipo: 'pronto'; conversa: Conversa })

const compacto = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })
const MES = () => new Date().toLocaleDateString('pt-BR', { month: 'long' })

// A marca do oneflow usa fontes próprias, iguais em todas as marcas do portal.
let fontesCarregadas = false
function carregarFontes() {
  if (fontesCarregadas) return
  fontesCarregadas = true
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Roboto:ital,wght@0,800;1,400&family=Yellowtail&display=swap'
  document.head.appendChild(link)
}

/** A marca: one (Poppins) flow (Yellowtail, em degradê) IA (selo magenta). */
export function MarcaOneflow({ grande }: { grande?: boolean }) {
  return <span className={`ofn-marca${grande ? ' g' : ''}`} aria-label="oneflow IA"><b>one</b><i>flow</i><em>IA</em></span>
}

const Estrela = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.8 4.6L18 9.4l-4.2 1.8L12 16l-1.8-4.8L6 9.4l4.2-1.8z" /><path d="M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
  </svg>
)

// Lembra se a pessoa deixou o painel grande: vale para os próximos dashboards e visitas.
const CHAVE_AMPLIADO = 'np-oneflow-ampliado'
function lerAmpliado(): boolean {
  try { return localStorage.getItem(CHAVE_AMPLIADO) === '1' } catch { return false }
}
function gravarAmpliado(v: boolean) {
  try { localStorage.setItem(CHAVE_AMPLIADO, v ? '1' : '0') } catch { /* sem armazenamento: só não lembra */ }
}

// Lembra se a pessoa fechou a nuvem do canto: continua fechada ao recarregar. Sobra só a estrela, para chamar o oneflow quando quiser.
const CHAVE_NUVEM_FECHADA = 'np-oneflow-nuvem-fechada'
function lerNuvemFechada(): boolean {
  try { return localStorage.getItem(CHAVE_NUVEM_FECHADA) === '1' } catch { return false }
}
function gravarNuvemFechada(v: boolean) {
  try { localStorage.setItem(CHAVE_NUVEM_FECHADA, v ? '1' : '0') } catch { /* sem armazenamento: só não lembra */ }
}

interface TopoProps {
  dashNome?: string
  aoFechar: () => void
  aoNova?: () => void
  saldo?: Saldo | null
  podeNova?: boolean
  ampliado?: boolean
  aoAmpliar?: () => void
}

function Topo({ dashNome, aoFechar, aoNova, saldo, podeNova, ampliado, aoAmpliar }: TopoProps) {
  return (
    <div className="ofn-topo">
      <div className="ofn-linha-topo">
        <div className="ofn-identidade">
          <div className="ofn-trade">TRADE</div>
          <MarcaOneflow grande />
        </div>
        <div className="ofn-acoes">
          {aoAmpliar && (
            <button type="button" className="ofn-icone ofn-ampliar" onClick={aoAmpliar} aria-pressed={!!ampliado}
                    aria-label={ampliado ? 'Diminuir o oneflow' : 'Aumentar o oneflow'} title={ampliado ? 'Diminuir' : 'Aumentar'}>
              {ampliado ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </button>
          )}
          <button type="button" className="ofn-icone" onClick={aoFechar} aria-label="Fechar o oneflow" title="Fechar"><CloseIcon fontSize="small" /></button>
        </div>
      </div>
      <p className="ofn-lema"><i>Ideias em movimento,</i> <b>respostas em instantes.</b></p>
      {(dashNome || aoNova) && (
        <div className="ofn-linha-contexto">
          {dashNome && <span className="ofn-contexto">Respondendo sobre <b>{dashNome}</b> · sua visão</span>}
          {aoNova && <button type="button" className="ofn-nova" onClick={aoNova} disabled={!podeNova}>Nova conversa</button>}
        </div>
      )}
      {saldo && (
        <div className="ofn-saldo" title="Tokens são a medida de uso da IA. O contador volta a zero no dia 1º.">
          {saldo.limitePessoa
            ? <>Você usou {compacto.format(saldo.usadoPessoa)} de {compacto.format(saldo.limitePessoa)} tokens em {MES()}</>
            : <>Você usou {compacto.format(saldo.usadoPessoa)} tokens em {MES()}</>}
          {saldo.limitePessoa ? <span className="ofn-barra"><span style={{ width: `${Math.min(100, Math.round(saldo.usadoPessoa * 100 / saldo.limitePessoa))}%` }} /></span> : null}
        </div>
      )}
    </div>
  )
}

/** Onde o oneflow ainda não está ligado: o que ele faz e com quem falar. */
function Convite({ dashNome }: { dashNome?: string }) {
  return (
    <div className="ofn-convite">
      <span className="ofn-bolha-fab"><Estrela /></span>
      <h3>Transforme seus dados em decisões com o Oneflow IA</h3>
      <p className="ofn-convite-status">O Oneflow IA ainda não está habilitado {dashNome ? <>para <b>{dashNome}</b></> : 'neste dashboard'}.</p>
      <p>Imagine ter as respostas que você precisa em segundos, sem precisar analisar gráficos ou criar relatórios.</p>
      <p>Com Oneflow, você conversa com seus dados em linguagem natural e descobre oportunidades, identifica tendências e toma decisões com mais agilidade.</p>
      <span className="ofn-convite-rotulo">Experimente perguntar:</span>
      <ul>
        <li>“Quais lojas precisam de mais atenção?”</li>
        <li>“O que mudou em relação ao mês passado?”</li>
        <li>“Onde estão nossas maiores oportunidades?”</li>
      </ul>
      <div className="ofn-convite-acao">
        <b>Pronto para dar o próximo passo?</b>
        <span>Fale com a equipe de BI da sua operação e descubra como ativar o Oneflow IA.</span>
      </div>
    </div>
  )
}

function Etapas({ andamento, aoParar }: { andamento: Andamento; aoParar: () => void }) {
  const etapas = andamento.etapas.length ? andamento.etapas : ['Entendendo a pergunta']
  return (
    <div className="ofn-msg ia">
      <span className="ofn-selo">IA</span>
      <div className="ofn-etapas" role="status">
        {etapas.map((e, i) => <span key={i} className={i === etapas.length - 1 ? 'agora' : 'feita'}>{e}</span>)}
        <small>{andamento.segundos} s{andamento.segundos >= 60 ? ' · perguntas que cruzam muitos dados podem levar alguns minutos' : ''}</small>
        <button type="button" className="ofn-parar" onClick={aoParar}>Parar de esperar</button>
        {andamento.raciocinio && <details><summary>Raciocínio do agente (só administradores)</summary><pre>{andamento.raciocinio}</pre></details>}
      </div>
    </div>
  )
}

type TamanhoDoPainel = Pick<TopoProps, 'ampliado' | 'aoAmpliar'>

function Chat({ dashId, ambienteId, dashNome, inicial, aoFechar, tamanho }: { dashId: number; ambienteId: number; dashNome?: string; inicial: Conversa; aoFechar: () => void; tamanho: TamanhoDoPainel }) {
  const c = useConversaOneflow(dashId, ambienteId, inicial)
  const [confirmarNova, setConfirmarNova] = useState(false)

  return (
    <>
      <Topo dashNome={dashNome} aoFechar={aoFechar} saldo={inicial.liberado ? c.saldo : null} {...tamanho}
            aoNova={() => setConfirmarNova(true)} podeNova={!c.ocupado && c.mensagens.length > 0} />
      {confirmarNova && (
        <div className="ofn-confirmar">
          <span>Começar uma nova conversa? A atual sai da tela.</span>
          <button type="button" onClick={() => { setConfirmarNova(false); c.novaConversa() }}>Começar</button>
          <button type="button" onClick={() => setConfirmarNova(false)}>Cancelar</button>
        </div>
      )}
      <div className="ofn-msgs">
        {c.mensagens.length === 0 && (
          <div className="ofn-msg ia"><span className="ofn-selo">IA</span><div className="ofn-bolha">Olá! O que você quer saber sobre {dashNome ? <b>{dashNome}</b> : 'este dashboard'}? Respondo com os dados que você vê.</div></div>
        )}
        {c.mensagens.map(m => {
          if (m.papel === 'user') return <div key={m.id} className="ofn-msg eu">{m.blocos.map(b => b.texto).join('\n')}</div>
          if (m.aviso) return <div key={m.id} className={`ofn-aviso ${m.aviso}`}>{m.blocos[0]?.texto}</div>
          return <div key={m.id} className="ofn-msg ia"><span className="ofn-selo">IA</span><div className="ofn-bolha"><Blocos blocos={m.blocos} /></div></div>
        })}
        {c.enviando && !c.andamento && <div className="ofn-msg ia"><span className="ofn-selo">IA</span><div className="ofn-etapas"><span className="agora">Enviando a pergunta</span></div></div>}
        {c.andamento && <Etapas andamento={c.andamento} aoParar={c.pararDeEsperar} />}
        <div ref={c.fimRef} />
      </div>
      {!c.ocupado && !c.bloqueado && c.sugestoes.length > 0 && (
        <div className="ofn-sugestoes">{c.sugestoes.map((s, i) => <button key={i} type="button" onClick={() => c.enviar(s)}>{s}</button>)}</div>
      )}
      {!inicial.liberado && <div className="ofn-faixa">Você ainda não está liberado para usar o oneflow neste dashboard. Peça a um administrador.</div>}
      {inicial.liberado && c.esgotado && <div className="ofn-faixa">O limite de uso do oneflow deste mês acabou. Ele volta no dia 1º.</div>}
      <form className="ofn-campo" onSubmit={e => { e.preventDefault(); c.enviar() }}>
        <textarea value={c.pergunta} onChange={e => c.setPergunta(e.target.value)} rows={1} maxLength={2000} disabled={c.bloqueado}
                  placeholder="Pergunte sobre os dados que você vê" aria-label="Pergunta para o oneflow"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); c.enviar() } }} />
        <button type="submit" className="ofn-enviar" disabled={c.bloqueado || c.ocupado || !c.pergunta.trim()} aria-label="Enviar">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      </form>
      <p className="ofn-rodape">Criado com IA. Pode errar: confira os números no dashboard.</p>
    </>
  )
}

/**
 * O oneflow no portal novo: botão flutuante no canto do dashboard e o painel ao lado, com a
 * identidade do oneflow (igual em todas as marcas). Aparece sempre: onde não há agente ligado,
 * o painel convida a pessoa a falar com a equipe responsável.
 */
export default function OneflowNovo({ children, dashId, ambienteId, dashNome, ligado }: {
  children: React.ReactNode
  dashId?: number
  ambienteId?: number | null
  dashNome?: string
  ligado: boolean
}) {
  const [aberto, setAberto] = useState(false)
  const [ampliado, setAmpliado] = useState(lerAmpliado)
  const [nuvemFechada, setNuvemFechada] = useState(lerNuvemFechada)
  const [estado, setEstado] = useState<Estado | null>(null)
  const chave = `${dashId}-${ambienteId}`

  const carregar = useCallback(async () => {
    setEstado({ chave, tipo: 'carregando' })
    const res = await services.api.oneflowConversa(dashId, ambienteId)
    setEstado(atual => {
      if (atual?.chave !== chave) return atual
      return res.fail ? { chave, tipo: 'erro', mensagem: res.error || 'Não consegui abrir o oneflow agora.' } : { chave, tipo: 'pronto', conversa: res.data }
    })
  }, [chave, dashId, ambienteId])

  const atual = estado?.chave === chave ? estado : null
  useEffect(() => { if (aberto && ligado && dashId && ambienteId && !atual) carregar() }, [aberto, ligado, dashId, ambienteId, atual, carregar])
  useEffect(() => { carregarFontes() }, [])

  const fechar = () => setAberto(false)
  const alternarTamanho = () => setAmpliado(v => { gravarAmpliado(!v); return !v })
  const fecharNuvem = (v: boolean) => { gravarNuvemFechada(v); setNuvemFechada(v) }
  const tamanho: TamanhoDoPainel = { ampliado, aoAmpliar: alternarTamanho }
  let painel: React.ReactNode = <><Topo dashNome={dashNome} aoFechar={fechar} {...tamanho} /><div className="ofn-vazio">Abrindo…</div></>
  if (!ligado) {
    painel = <><Topo dashNome={dashNome} aoFechar={fechar} {...tamanho} /><Convite dashNome={dashNome} /></>
  } else if (atual?.tipo === 'erro') {
    painel = <><Topo dashNome={dashNome} aoFechar={fechar} {...tamanho} /><div className="ofn-vazio">{atual.mensagem}<button type="button" onClick={carregar}>Tentar de novo</button></div></>
  } else if (atual?.tipo === 'pronto' && dashId && ambienteId) {
    const c = atual.conversa
    if (c.disponivel) painel = <Chat key={chave} dashId={dashId} ambienteId={ambienteId} dashNome={dashNome} inicial={c} aoFechar={fechar} tamanho={tamanho} />
    else if (c.legado) painel = <><Topo dashNome={dashNome} aoFechar={fechar} {...tamanho} /><div className="ofn-legado"><ChatLegado key={chave} dashId={dashId} environmentId={ambienteId} /></div></>
    else painel = <><Topo dashNome={dashNome} aoFechar={fechar} {...tamanho} /><Convite dashNome={dashNome} /></>
  }

  return (
    <div className={`ofn-casca${aberto ? ' aberto' : ''}`}>
      <div className="ofn-conteudo">{children}</div>
      {dashId && !aberto && !nuvemFechada && (
        <div className="ofn-fab-grupo">
          <button type="button" className="ofn-fab" onClick={() => setAberto(true)} aria-label="Abrir o Trade oneflow IA">
            <span className="ofn-bolha-fab"><Estrela /></span>
            <span><MarcaOneflow /><small>{ligado ? 'Pergunte sobre este dashboard' : 'Respostas sobre seus dados'}</small></span>
          </button>
          <button type="button" className="ofn-fab-x" onClick={() => fecharNuvem(true)} aria-label="Fechar a nuvem do oneflow" title="Fechar">
            <CloseIcon style={{ fontSize: 14 }} />
          </button>
        </div>
      )}
      {dashId && !aberto && nuvemFechada && (
        <button type="button" className="ofn-fab-mini" onClick={() => { fecharNuvem(false); setAberto(true) }}
                aria-label="Abrir o Trade oneflow IA" title="Abrir o oneflow IA">
          <Estrela />
        </button>
      )}
      {dashId && <aside className={`ofn${ampliado ? ' ampliado' : ''}`} hidden={!aberto} aria-label="Trade oneflow IA">{painel}</aside>}
    </div>
  )
}
